
import { Event, EventPublisher } from 'pubsub/eventsV2'
import { schedule } from 'utils/schedule'
import { TealiumPageViewEvent, TealiumStandardEvent } from '.'

export type TealiumSessionId = {
  sessionId?: string
}

export type TealiumSubscriberTealiumActivity = (
  (TealiumSessionId & TealiumPageViewEvent) |
  (TealiumSessionId & TealiumStandardEvent)
)

const subscribe = (eventPublisher: EventPublisher) => {
  return eventPublisher.subscribe({
    next: (event: Event) => {
      if (!event.tealium) {
        return
      }
      const { additionalData, ...data } = event.tealium
      // call via the exported const so that we can appropriately spyOn this call in jest for testing
      tealiumEventSubscriber.sendOrScheduleTealiumEvent({
        sessionId: event.sessionId,
        ...data,
        ...additionalData
      })
    }
  })
}

export const isTealiumPageViewActivity = (event: TealiumSubscriberTealiumActivity): event is TealiumPageViewEvent => {
  return (event as TealiumPageViewEvent).page_name !== undefined
}

/**
 * Sends a tealium event without checking if utag is set. This means it doesn't queue.
 * @param event
 */
const sendTealiumEvent = (event: TealiumSubscriberTealiumActivity) => {
  if (isTealiumPageViewActivity(event)) {
    window.utag?.view(event)
  } else {
    window.utag?.link(event)
  }
}

const tealiumQueue: TealiumSubscriberTealiumActivity[] = []
let scheduledTealiumEventCallback = false
let isQueuingEvents = false

/**
 * Sends the tealium event. If utag is not set, this function automatically queues it to be sent. In some cases, if the utag is set, we may still queue the event. This is so events are sent in the order they were originally recceived by the subscriber
 * @param event
 * @returns
 */
const sendOrScheduleTealiumEvent = (event: TealiumSubscriberTealiumActivity) => {
  if (window.utag && !isQueuingEvents) {
    sendTealiumEvent(event)
    return
  }

  tealiumQueue.push(event)
  if (!scheduledTealiumEventCallback) {
    scheduledTealiumEventCallback = true
    isQueuingEvents = true
    schedule(
      () => Boolean(window.utag),
      () => {
        isQueuingEvents = false
        tealiumQueue.forEach(sendTealiumEvent)
      },
      2500,
      10,
      'Tealium event queuing'
    )
  }
}

export const tealiumEventSubscriber = {
  subscribe,
  sendOrScheduleTealiumEvent
}
